/**
 * SidebarItem — a single navigation entry rendered in the 21st.dev rail
 * style: a fixed icon with a label that slides/fades in as the rail expands.
 *
 * A leaf entry is a <NavLink>; an entry with `children` is an inline
 * collapsible group (groups only expand while the rail itself is expanded).
 */

import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { Icons } from '@/src/modules/components/Icons';
import { Badge } from '@/src/modules/components/ui/badge';
import type { NavItem } from '@/src/infrastructure/config/navigation';

/** Label motion — slides + fades together with the rail width. */
export const labelVariants = {
  open: {
    x: 0,
    opacity: 1,
    transition: { x: { stiffness: 1000, velocity: -100 } },
  },
  closed: {
    x: -20,
    opacity: 0,
    transition: { x: { stiffness: 100 } },
  },
};

const rowBase =
  'flex h-8 w-full flex-row items-center rounded-md px-2 py-1.5 ' +
  'text-muted-foreground transition hover:bg-muted hover:text-primary ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** True when any descendant route of `item` matches the current location. */
function isGroupActive(item: NavItem, pathname: string): boolean {
  return !!item.children?.some(
    (c) =>
      (c.path && (pathname === c.path || pathname.startsWith(c.path + '/'))) ||
      isGroupActive(c, pathname),
  );
}

function ItemLabel({ item }: { item: NavItem }) {
  return (
    <span className="ml-2 flex items-center gap-2 text-sm font-medium">
      {item.label}
      {item.badge && (
        <Badge
          variant="outline"
          className="h-fit border-none bg-primary/10 px-1.5 py-0 text-[10px] text-primary"
        >
          {item.badge}
        </Badge>
      )}
    </span>
  );
}

type SidebarItemProps = {
  item: NavItem;
  /** Icon-only rail. Always false inside the mobile drawer. */
  collapsed: boolean;
  /** Nesting depth — drives child indentation. */
  depth?: number;
  /** Called after a navigation — used to close the mobile drawer. */
  onNavigate?: () => void;
  /**
   * React list key. Declared explicitly because this project ships without
   * `@types/react`, so `JSX.IntrinsicAttributes` (the usual source of `key`)
   * is unavailable on strongly-typed component props.
   */
  key?: string | number;
};

export function SidebarItem({
  item,
  collapsed,
  depth = 0,
  onNavigate,
}: SidebarItemProps): React.ReactElement {
  const location = useLocation();
  const hasChildren = !!item.children?.length;
  const groupActive = hasChildren && isGroupActive(item, location.pathname);
  const [open, setOpen] = useState(groupActive);

  useEffect(() => {
    if (groupActive) setOpen(true);
  }, [groupActive]);

  // --- Leaf entry -----------------------------------------------------------
  if (!hasChildren) {
    return (
      <NavLink
        to={item.path!}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            rowBase,
            depth > 0 && 'pl-8',
            isActive && 'bg-muted font-medium text-primary',
          )
        }
      >
        <item.icon className="h-4 w-4 shrink-0" />
        <motion.li variants={labelVariants} className="flex">
          {!collapsed && <ItemLabel item={item} />}
        </motion.li>
      </NavLink>
    );
  }

  // --- Group entry ----------------------------------------------------------
  return (
    <div>
      <button
        type="button"
        onClick={() => !collapsed && setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(rowBase, groupActive && 'text-primary')}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        <motion.li variants={labelVariants} className="flex w-full items-center">
          {!collapsed && (
            <>
              <ItemLabel item={item} />
              <Icons.ChevronDown
                className={cn(
                  'ml-auto h-4 w-4 transition-transform duration-200',
                  open && 'rotate-180',
                )}
              />
            </>
          )}
        </motion.li>
      </button>

      {/* Inline expand/collapse via grid-template-rows (0fr → 1fr). */}
      {!collapsed && (
        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-300 ease-in-out',
            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-1 pt-1">
              {item.children!.map((child) => (
                <SidebarItem
                  key={child.id}
                  item={child}
                  collapsed={false}
                  depth={depth + 1}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
